import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { fundTypeLabel } from '@/components/shared/fund-type-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime, formatMoney } from '@/utils/format';

interface FundSummary {
  id: string;
  name: string;
  fundType: string;
  customTypeId: string | null;
  customTypeName: string | null;
  isActive: boolean;
  defaultCurrency: string;
  givenMinor: number;
  spentMinor: number;
  balanceMinor: number;
  createdAt: string;
}

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

export const FundSummaryDialog = ({
  fundId,
  open,
  onOpenChange,
}: {
  fundId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);

  const fund = useQuery({
    queryKey: ['giving', 'funds', fundId],
    queryFn: () => api.get<FundSummary>(`/giving/funds/${fundId}`),
    enabled: open && Boolean(fundId),
  });

  const createdAt = fund.data?.createdAt ?? null;
  const createdTitleDate = createdAt
    ? formatDate(createdAt.slice(0, 10), { locale, style: 'medium' })
    : null;
  const createdDateTime = createdAt
    ? formatDateTime(createdAt, { locale, timezone, withZone: true })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>
            {createdTitleDate
              ? t('funds.detail.titleDated', { date: createdTitleDate })
              : t('funds.detail.title')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {fund.isLoading ? (
            <div className="space-y-3" aria-busy>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : fund.data ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-body font-medium text-fg">{fund.data.name}</p>
                {!fund.data.isActive ? (
                  <Badge variant="neutral">{t('funds.inactive')}</Badge>
                ) : null}
              </div>
              <dl>
                {createdDateTime ? (
                  <DetailRow label={t('funds.detail.created')}>{createdDateTime}</DetailRow>
                ) : null}
                <DetailRow label={t('funds.type')}>
                  {fundTypeLabel(
                    {
                      fundType: fund.data.fundType,
                      customTypeName: fund.data.customTypeName,
                    },
                    (key) => t(key),
                  )}
                </DetailRow>
                <DetailRow label={t('funds.detail.total')}>
                  {formatMoney(fund.data.givenMinor, fund.data.defaultCurrency, { locale })}
                </DetailRow>
                <DetailRow label={t('funds.detail.spent')}>
                  {formatMoney(fund.data.spentMinor, fund.data.defaultCurrency, { locale })}
                </DetailRow>
                <DetailRow label={t('funds.detail.balance')}>
                  {formatMoney(fund.data.balanceMinor, fund.data.defaultCurrency, { locale })}
                </DetailRow>
              </dl>
            </div>
          ) : (
            <p className="text-body-sm text-fg-secondary">{t('funds.empty')}</p>
          )}
        </DialogBody>
        <DialogFooter className="justify-end">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
